import { memo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Box, Text } from '@zillow/constellation-10';

import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';
import { DayOfWeek, FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyEditV2WebApiHandler_contactHours,
  useRentalManagerUserPropertyEditV2WebApiHandler_leaseTerms,
  useRentalManagerUserPropertyEditV2WebApiHandler_name,
  useRentalManagerUserPropertyEditV2WebApiHandler_unitsInBuilding,
  useRentalManagerUserPropertyEditV2WebApiHandler_webSite,
  useRentalManagerUserPropertyMultiEditWebApiHandler_multi,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import typeCheckUtils from '../../../../utils/typeCheckUtils';
import { getIsInputFormDisabled } from '../../utils';

import { MAX_NUM_UNITS_ERROR, MAX_NUM_UNITS_VALUE } from '../../../../constants/AddPropertyConstants';
import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';

import { useAuth } from '../../../AuthProvider';
import RecommendationsAlert from '../../../PostingPath/RecommendationsAlert';
import RMWInput from '../../../RMWInput';
import LogoUpload from './LogoUpload';
import type { DatarouterError } from '@zg-rentals/web-base';
type Props = {
  onRecsClick: (recommendationType: string) => void;
};

const appointmentsObject = [
  { label: 'Sunday', name: 'contactSunday', key: 'sunday' },
  { label: 'Monday', name: 'contactMonday', key: 'monday' },
  { label: 'Tuesday', name: 'contactTuesday', key: 'tuesday' },
  { label: 'Wednesday', name: 'contactWednesday', key: 'wednesday' },
  { label: 'Thursday', name: 'contactThursday', key: 'thursday' },
  { label: 'Friday', name: 'contactFriday', key: 'friday' },
  { label: 'Saturday', name: 'contactSaturday', key: 'saturday' },
];

const numUnitsFieldName = 'numUnits';

const BuildingDetailsContent = ({ onRecsClick }: Props): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const name = listingDetails?.name || '';
  const numUnits = listingDetails?.numUnits;
  const leaseTerms = listingDetails?.leaseTerms || '';
  const website = listingDetails?.website || '';
  const description = listingDetails?.fullDescription || '';
  const contactHours = listingDetails?.contactHours || {};
  const productType = listingDetails?.adproduct?.productTypes?.[0] || '';
  const isSelfServeOnboardingInProgress =
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress || false;
  const SSOBProductType = listingDetails?.selfServeOnboardingStatus?.productType || '';

  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [errorObj, setErrorObj] = useState<Record<string, string>>({});

  const prevFieldValueOnFocus = useRef({
    value: '',
    errorMessage: '',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);
  // Name, website, and lease terms can be edited as feed overrides when the Building details section is enabled.
  // The other fields stay manual-only because the backend has no feed override for them yet.
  const isFeedOverrideFieldDisabled = useIsInputFormDisabled(
    listingDetails,
    canUpdateListing,
    FeedOverrideSection.BUILDING_DETAILS,
  );

  const displayAlertForWebsiteField =
    isInputFormDisabled === false &&
    ((isSelfServeOnboardingInProgress && SSOBProductType === AdproductsConstants.ProductCodes.BASE_PACKAGE) ||
      productType === AdproductsConstants.ProductCodes.BASE_PACKAGE);

  const {
    mutateAsync: updateName,
    error: nameError,
    reset: resetNameError,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_name,
    successToastMessage: 'Property name updated',
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_name', details: { listingAlias } } },
  });

  const {
    mutateAsync: updateNumUnits,
    error: numUnitsError,
    reset: resetNumUnitsError,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_unitsInBuilding,
    successToastMessage: 'Units in building updated',
    meta: {
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_unitsInBuilding', details: { listingAlias } },
    },
  });

  const {
    mutateAsync: updateLeaseTerms,
    error: leaseTermsError,
    reset: resetLeaseTermsError,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_leaseTerms,
    successToastMessage: 'Lease terms updated',
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_leaseTerms', details: { listingAlias } } },
  });

  const {
    mutateAsync: updateWebsite,
    error: websiteError,
    reset: resetWebsiteError,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_webSite,
    successToastMessage: 'Website updated',
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_webSite', details: { listingAlias } } },
  });

  const {
    mutateAsync: updateContactHours,
    error: contactHoursError,
    reset: resetContactHoursError,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_contactHours,
    successToastMessage: 'Business hours updated',
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_contactHours', details: { listingAlias } } },
  });

  const {
    mutateAsync: updateDescription,
    error: descriptionError,
    reset: resetDescriptionError,
  } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyMultiEditWebApiHandler_multi,
    successToastMessage: 'Property description updated',
    meta: { context: { api: 'RentalManagerUserPropertyMultiEditWebApiHandler_multi', details: { listingAlias } } },
  });

  const save = async (eventOrValue: string | { target: { name: string; value: string } }) => {
    // Handle both string value and event object for compatibility with RMWInput
    const event = typeof eventOrValue === 'string' ? { target: { name: '', value: eventOrValue } } : eventOrValue;

    const { name: fieldName, value } = event.target;

    if (prevFieldValueOnFocus.current.value === value) {
      if (prevFieldValueOnFocus.current.errorMessage) {
        setErrorObj({ ...errorObj, [fieldName]: prevFieldValueOnFocus.current.errorMessage });
      }

      return;
    }

    if (fieldName === numUnitsFieldName) {
      const errorMessage = 'Units in building must be a whole number';
      const numericValue = typeCheckUtils.Number(value);

      if ((!numericValue && numericValue !== 0) || !Number.isInteger(numericValue)) {
        setErrorObj({ ...errorObj, [fieldName]: errorMessage });
        return;
      }

      if (Number(numericValue) > MAX_NUM_UNITS_VALUE) {
        setErrorObj({ ...errorObj, [fieldName]: MAX_NUM_UNITS_ERROR });
        return;
      }
    }

    // Clear any previous field errors
    setErrorObj({ ...errorObj, [fieldName]: '' });

    try {
      if (fieldName === 'name') {
        await updateName({ postData: { name: value, listingAlias } });
      } else if (fieldName === numUnitsFieldName) {
        await updateNumUnits({ postData: { unitsInBuilding: Number(value), listingAlias } });
      } else if (fieldName === 'description') {
        await updateDescription({
          postData: { property: { listingAlias, fullDescription: value } },
        });
      } else if (fieldName === 'leaseTerms') {
        await updateLeaseTerms({ postData: { leaseTerms: value, listingAlias } });
      } else if (fieldName === 'website') {
        await updateWebsite({ postData: { webSite: value, listingAlias } });
      } else if (fieldName.includes('contact')) {
        // contactHours API expects dayOfWeek enum
        const dayOfWeek = fieldName.split('contact').pop()?.toUpperCase() as keyof typeof DayOfWeek;
        await updateContactHours({
          postData: {
            listingAlias,
            dayOfWeek: DayOfWeek[dayOfWeek],
            contactHours: value,
          },
        });
      }
    } catch (err) {
      const message = (err as DatarouterError)?.message ?? 'An error occurred';
      setErrorObj({ ...errorObj, [fieldName]: message });
    }
  };

  const handleFocus = (event: { target: { name: string; value: string } }) => {
    const { name: fieldName, value } = event.target;

    prevFieldValueOnFocus.current.value = value;
    prevFieldValueOnFocus.current.errorMessage = errorObj[fieldName];
  };

  const clearFieldError = (event: { target: { name: string } }) => {
    const { name: fieldName } = event.target;

    setErrorObj({ ...errorObj, [fieldName]: '' });

    if (fieldName === 'name') resetNameError();
    else if (fieldName === numUnitsFieldName) resetNumUnitsError();
    else if (fieldName === 'description') resetDescriptionError();
    else if (fieldName === 'leaseTerms') resetLeaseTermsError();
    else if (fieldName === 'website') resetWebsiteError();
    else if (fieldName.includes('contact')) resetContactHoursError();
  };

  return (
    <Box data-testid="BuildingDetailsContent-Edit" css={{ display: 'flex', gap: '600', flexDirection: 'column' }}>
      <RecommendationsAlert
        sectionName={REVIEW_PAGE_SECTIONS.BUILDING_DETAILS}
        onRecsClick={(recKey) => onRecsClick(recKey)}
      />

      <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' }, gap: '400' }}>
        <RMWInput
          disabled={isFeedOverrideFieldDisabled}
          isRequired={!isFeedOverrideFieldDisabled}
          errorMessage={errorObj.name || (nameError as DatarouterError)?.message}
          fieldName="name"
          flex="1 1 50%"
          initialValue={name}
          label="Property name"
          clearFieldError={clearFieldError}
          onBlur={save}
          onFocus={handleFocus}
        />

        <RMWInput
          disabled={isInputFormDisabled}
          errorMessage={errorObj.numUnits || (numUnitsError as DatarouterError)?.message}
          fieldName={numUnitsFieldName}
          flex="1 1 50%"
          initialValue={isFinite(Number(numUnits)) ? String(numUnits) : ''}
          label="Units in building"
          clearFieldError={clearFieldError}
          onBlur={save}
          onFocus={handleFocus}
        />
      </Box>

      <RMWInput
        disabled={isInputFormDisabled}
        isRequired={!isInputFormDisabled}
        errorMessage={errorObj.description || (descriptionError as DatarouterError)?.message}
        fieldName="description"
        initialValue={description}
        inputType="textarea"
        label="Property description"
        maxLength="1999"
        placeholder="What is awesome about your place?"
        clearFieldError={clearFieldError}
        onBlur={save}
        onFocus={handleFocus}
        showCharacterCount
      />

      <RMWInput
        disabled={isFeedOverrideFieldDisabled}
        errorMessage={errorObj.leaseTerms || (leaseTermsError as DatarouterError)?.message}
        fieldName="leaseTerms"
        initialValue={leaseTerms}
        inputType="textarea"
        label="Lease terms"
        maxLength="150"
        placeholder="Example: Renter is responsible for gas and electric"
        clearFieldError={clearFieldError}
        onBlur={save}
        onFocus={handleFocus}
        showCharacterCount
      />

      <RMWInput
        disabled={isFeedOverrideFieldDisabled}
        errorMessage={errorObj.website || (websiteError as DatarouterError)?.message}
        fieldName="website"
        initialValue={website}
        label="Website URL"
        clearFieldError={clearFieldError}
        onBlur={save}
        onFocus={handleFocus}
        placeholder={''}
      />

      {displayAlertForWebsiteField && (
        <Alert data-testid="Alert-WebsiteURL" tone="info">
          The website URL will not be displayed on your listing. Contact your Zillow representative to upgrade your
          listing and unlock this feature.
        </Alert>
      )}

      <LogoUpload />

      <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
        <Text textStyle="body-lg-bold" css={isInputFormDisabled ? { opacity: 'disabled' } : undefined}>
          Business hours
        </Text>

        {appointmentsObject.map(({ name: appointmentName, label, key }) => (
          <Box css={{ display: 'flex' }} key={key}>
            <RMWInput
              disabled={isInputFormDisabled}
              errorMessage={
                errorObj[appointmentName] ||
                (appointmentName.includes('contact') ? (contactHoursError as DatarouterError)?.message : undefined)
              }
              fieldName={appointmentName}
              flex={{ xl: '0 0 40%', default: 'auto' }}
              initialValue={contactHours[key as keyof typeof contactHours] || ''}
              label={label}
              placeholder={isInputFormDisabled ? '' : '8:00am to 5:00pm'}
              onBlur={save}
              onFocus={handleFocus}
              clearFieldError={clearFieldError}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default memo(BuildingDetailsContent);
